import { Button } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import AppHeader from "@saleor/components/AppHeader";
import { CardSpacer } from "@saleor/components/CardSpacer";
import { Container } from "@saleor/components/Container";
import PageHeader from "@saleor/components/PageHeader";
import useNavigator from "@saleor/hooks/useNavigator";
import useUser from "@saleor/hooks/useUser";
import { sectionNames } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import RemittanceRunnerCancelModal from "@saleor/remittanceRunner/components/RemittanceRunnerCancelModal";
import RemittanceRunnerHistory from "@saleor/remittanceRunner/components/RemittanceRunnerHistory";
import RemittanceRunnerOrderDetails from "@saleor/remittanceRunner/components/RemittanceRunnerOrderDetails";
import RemittanceRunnerRemittanceInfo from "@saleor/remittanceRunner/components/RemittanceRunnerRemittanceInfo";
import RemittanceRunnerRemittanceProof from "@saleor/remittanceRunner/components/RemittanceRunnerRemittanceProof";
import RemittanceRunnerRemitter from "@saleor/remittanceRunner/components/RemittanceRunnerRemitter";
import { useGetRemittance } from "@saleor/remittanceRunner/queries";
import { remittanceRunnerListPath } from "@saleor/remittanceRunner/url";
import {
  getOrderManuallyAdjustedAmount,
  isOrderManuallyAdjusted,
  isRunnerRider
} from "@saleor/remittanceRunner/utils/handlers";
import { RemittanceStatusEnum } from "@saleor/types/globalTypes";
import moment from "moment-timezone";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles(
  theme => ({
    cancelButton: {
      "& span": {
        color: "#002972"
      },
      "&:hover": {
        backgroundColor: "#EC5D77"
      },
      backgroundColor: "#EC5D77",
      float: "right",
      fontWeight: 800,
      marginTop: -50,
      overflow: "hidden"
    },
    cancelButtonDisable: {
      "& span": {
        color: "#FFF"
      },
      "&:hover": {
        backgroundColor: "#BDBDBD"
      },
      backgroundColor: "#BDBDBD"
    },

    date: {
      marginBottom: theme.spacing(3)
    },
    grid: {
      paddingTop: "20px"
    },
    header: {
      display: "flex",
      marginBottom: 15
    },
    headerHolder: {
      display: "inline-flex"
    },
    pageStyle: {
      marginTop: "30px"
    },
    textTitle: {
      fontSize: "18px",
      fontWeight: 500,
      padding: "0px 0px 10px 0px",
      whiteSpace: "nowrap"
    }
  }),
  {
    name: "RemittanceRunnerDetails"
  }
);

export interface RemittanceRunnerDetailsPageProps {
  id: string;
}

const RemittanceRunnerDetails: React.FC<RemittanceRunnerDetailsPageProps> = props => {
  const { id } = props;
  const classes = useStyles(props);
  const intl = useIntl();
  const user = useUser();
  const navigate = useNavigator();

  const { data: remittance, refetch } = useGetRemittance({
    displayLoader: true,
    variables: { id }
  });

  const isDispatcher =
    user?.user?.userType?.code && isRunnerRider(user?.user?.userType?.code);
  const isToRemit =
    remittance?.remittance?.status === RemittanceStatusEnum.TO_REMIT;

  const disableCancel =
    isToRemit &&
    remittance?.remittance?.isProcessing &&
    remittance?.remittance?.note !== undefined;

  const [displayDialog, setDisplayDialog] = React.useState(false);

  return (
    <Container>
      <AppHeader onBack={() => navigate(remittanceRunnerListPath)}>
        {intl.formatMessage(sectionNames.remittanceRunner)}
      </AppHeader>

      <div className={classes.headerHolder}>
        <PageHeader
          className={classes.header}
          inline
          title={remittance?.remittance?.remittanceNo}
        />
      </div>

      <Grid spacing={2} container className={classes.grid}>
        <Grid xs={12} md={8} item>
          {/* Remittance Info */}
          <RemittanceRunnerRemittanceInfo
            dateRemitted={
              maybe(() => remittance?.remittance?.remittedAt)
                ? moment(remittance?.remittance?.remittedAt).format(
                    "MMM DD, YYYY hh:mm A"
                  )
                : "-----"
            }
            remittanceBranch="BDO" // STATIC
            agingTime={remittance?.remittance?.aging} // TIME
            remittanceAmount={remittance?.remittance?.totalAmount}
            status={remittance?.remittance?.status}
          />
          <CardSpacer />
          <div className={classes.textTitle}>
            <FormattedMessage
              defaultMessage="Order Details"
              description="Order Details"
            />
          </div>

          {/* Order Details */}
          {remittance?.remittance?.remittanceLines.map(remittanceLine => (
            <>
              <RemittanceRunnerOrderDetails
                orderCode={remittanceLine?.order?.orderCode}
                orderPrice={
                  isOrderManuallyAdjusted(remittanceLine?.order)
                    ? getOrderManuallyAdjustedAmount(remittanceLine?.order)
                    : remittanceLine?.order?.total?.gross?.amount
                }
              />
              <CardSpacer />
            </>
          ))}

          {/* History */}
          <RemittanceRunnerHistory
            events={remittance?.remittance?.remittanceEvents}
          />
          <CardSpacer />
        </Grid>

        <Grid xs={12} md={4} item>
          {/* Restrict other users to Cancel Remittance other than Dispatcher account only and if in TO REMIT stage. */}
          {/* Disable the button if in the process of validating or the rider is remitting. */}
          {maybe(() => isDispatcher && isToRemit) && (
            <>
              <Button
                className={
                  disableCancel
                    ? `${classes.cancelButton} ${classes.cancelButtonDisable}`
                    : classes.cancelButton
                }
                id="remittanceCancelBtn"
                onClick={() => setDisplayDialog(true)}
                disabled={disableCancel}
              >
                <FormattedMessage
                  defaultMessage="Cancel Remittance"
                  description="button"
                />
              </Button>

              <RemittanceRunnerCancelModal
                remittanceID={remittance?.remittance?.id}
                remittanceNo={remittance?.remittance?.remittanceNo}
                displayDialog={displayDialog}
                onClose={() => setDisplayDialog(false)}
              />
            </>
          )}

          {/* Remitter */}
          <RemittanceRunnerRemitter
            remitterName={
              remittance?.remittance?.remitter?.firstName +
              " " +
              remittance?.remittance?.remitter?.lastName
            }
            remitterContactNum={remittance?.remittance?.remitter?.contactNo}
            remitterEmail={remittance?.remittance?.remitter?.email}
          />
          <CardSpacer />

          {/* Proof of Remittance */}
          <RemittanceRunnerRemittanceProof
            isDispatcher={isDispatcher}
            remittanceID={remittance?.remittance?.id}
            receiptImage={remittance?.remittance?.receiptImage}
            refetch={refetch}
          />
          <CardSpacer />
        </Grid>
      </Grid>
    </Container>
  );
};
RemittanceRunnerDetails.displayName = "RemittanceRunnerDetails";
export default RemittanceRunnerDetails;
